import { observer } from 'mobx-react';
import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { DebouncedFunc } from 'lodash';

import { useSearchParams } from 'react-router-dom';

import { Drawer } from '@mui/material';

import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityFieldRoles, EnumOptions, ImageType } from 'utils/api/modules/entities/entities.types';
import { UuidableName } from 'utils/api/types';

import { useStore } from 'utils/hooks/store';

import { EntityModules, ProtectorType } from 'core/constants';

import AuditQuestionDrawer, { AuditQuestionDrawerTab } from 'components/Audits/AuditQuestionDrawer';
import {
  closeQuestionParams,
  drawerTabFromParam,
  openQuestionParams,
  QUESTION_DRAWER_PAPER_PROPS,
  QUESTION_PARAM,
  ROW_BUTTONS,
  rowButtonLabel,
  TAB_PARAM,
} from 'components/Audits/questionDrawer';
import {
  LookupAnswerEntry,
  lookupAnswerEntries,
  lookupEntryUuids,
  resolveLookupEntries,
} from 'components/Audits/lookupRecords';
import { ActionButton, ActionButtons, CountBadge } from 'components/Audits/styles';

import { getEntitySubmissionConfig, isAuditsSubmission } from '../../config';
import { SectionHeader, QuestionsList, QuestionLabelRow, QuestionTitle, SectionDescription } from './styles';
import EntitiesFormField from '../EntitiesFormField/EntitiesFormField';
import RepeatingGroupField, { RepeatingProperties } from '../RepeatingGroupField';
import { normalizeAnswerForSave } from './normalizeAnswer';

type DebouncedUpdate = DebouncedFunc<(type: ProtectorType, questionKey: string, value: AnswerTypes) => Promise<void>>;

const SubmissionSection: React.FC = (): React.ReactElement => {
  const {
    entitiesFormSubmission: {
      data, // Add data observation to trigger re-renders
      contentErrors, // Observe contentErrors to trigger re-renders when errors change
      entityType,
      questionActivities,
      getCurrentSectionProperties,
      getContentForKey,
      getErrorForKey,
      updateContentForKey,
      updateContentForKeyOffline,
      requiredNotFilled,
      repeatingGroupErrors,
      revalidateRepeatingGroup,
      markEditPending,
      isPublicSubmission,
      setPendingFiles,
    },
  } = useStore();

  // The open question lives in the URL so a drawer is linkable and survives a reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const openQuestionKey = searchParams.get(QUESTION_PARAM);
  const openTab = drawerTabFromParam(searchParams.get(TAB_PARAM));

  const section = getCurrentSectionProperties();
  const isAudits = isAuditsSubmission(entityType);
  const config = getEntitySubmissionConfig(entityType);
  // `section.order` changes identity on every draft refresh; the section id does not.
  const sectionKey = data?.stack[data.stack.length - 1];

  // Create a memoized map of debounced update functions for each field
  const debouncedUpdates = useMemo(() => {
    if (!section) {
      return {};
    }

    const updates: Record<string, DebouncedUpdate> = {};

    section.order.forEach((questionKey) => {
      updates[questionKey] = debounce(
        async (type: ProtectorType, key: string, value: AnswerTypes): Promise<void> => {
          // when isLookup is True, and isPublicSubmission is True, we don't want to do anything with the value
          // when isLookup is True, and isPublicSubmission is False, we want to convert the value to the uuid
          if (isPublicSubmission) {
            await updateContentForKeyOffline(key, value);

            return;
          }

          await updateContentForKey(key, normalizeAnswerForSave(type, value), type);
        },
        // we do not want to debounce public submissions
        isPublicSubmission ? 0 : 1000
      );
    });

    return updates;
  }, [sectionKey, updateContentForKey, updateContentForKeyOffline, isPublicSubmission]);

  // Flush, not cancel: a dropped debouncer would lose the edit and strand the saving indicator.
  // Layout cleanup, as a passive one runs after the container's reset() on unmount and finds no draft.
  useLayoutEffect(
    () => () => {
      Object.values(debouncedUpdates).forEach((update) => update.flush());
    },
    [debouncedUpdates]
  );

  React.useEffect(() => {
    questionActivities?.loadStats();
  }, [questionActivities]);

  if (!section) {
    return <></>;
  }

  const openDrawer = (questionKey: string, tab: AuditQuestionDrawerTab): void =>
    setSearchParams(openQuestionParams(searchParams, questionKey, tab), { replace: true });

  const closeDrawer = (): void => setSearchParams(closeQuestionParams(searchParams), { replace: true });

  const lookupAnswerFor = (questionKey: string): LookupAnswerEntry[] =>
    lookupAnswerEntries(section.properties[questionKey]?.questionType, data?.content?.[questionKey]);

  // Between the optimistic write and the autosave response these are bare uuids, so resolve them
  // from the cache — dropping them would empty the tab and re-offer records the answer carries.
  const lookupRelationsFor = (questionKey: string): UuidableName[] =>
    resolveLookupEntries(lookupAnswerFor(questionKey));

  return (
    <>
      <SectionHeader>{section.title}</SectionHeader>
      <SectionDescription>{section.description}</SectionDescription>
      <QuestionsList data-testid={isAudits ? 'audit-fillout-section' : undefined}>
        {section.order.map((questionKey) => {
          const questionType = section.properties[questionKey].questionType as ProtectorType;
          const lookupType = section.properties[questionKey].properties?.lookupType as unknown as EntityModules;
          const description = section.properties[questionKey].description || '';
          const image = section.properties[questionKey].properties?.image as unknown as ImageType;
          const value = getContentForKey(questionKey);
          const error = getErrorForKey(questionKey);
          const roles = section.properties[questionKey].properties?.roles as unknown as EntityFieldRoles[] | undefined;
          const noFutureDate = roles?.includes(EntityFieldRoles.NO_FUTURE_DATE) ?? false;
          const multiple = !!section.properties[questionKey].properties?.multiple;
          const stats = questionActivities?.statsFor(questionKey);
          const answerRecords = lookupEntryUuids(lookupAnswerFor(questionKey)).length;

          const label = (
            <QuestionTitle $required={section.required.includes(questionKey)}>
              {section.properties[questionKey].description}
            </QuestionTitle>
          );

          // Events keeps the bare label: the h2's UA margin cannot collapse out of a flex row,
          // so wrapping it there would shift the spacing on every event form.
          const title = isAudits ? (
            <QuestionLabelRow>
              {label}
              {questionActivities && (
                <ActionButtons>
                  {ROW_BUTTONS.map((button) => {
                    const count = stats ? button.count(stats, answerRecords) : 0;

                    return (
                      <ActionButton
                        key={button.kind}
                        type="button"
                        data-testid="audit-question-activity-badge"
                        data-badge-kind={button.kind}
                        aria-label={rowButtonLabel(button.label, count)}
                        onClick={() => openDrawer(questionKey, button.tab)}
                      >
                        <button.Icon />
                        {button.label}
                        {count > 0 && <CountBadge>{count}</CountBadge>}
                      </ActionButton>
                    );
                  })}
                </ActionButtons>
              )}
            </QuestionLabelRow>
          ) : (
            label
          );

          if (questionType === ProtectorType.RepeatingGroup) {
            return (
              <div
                key={questionKey}
                data-testid={isAudits ? 'audit-question-row' : undefined}
                data-question-key={isAudits ? questionKey : undefined}
              >
                {title}
                <RepeatingGroupField
                  questionKey={questionKey}
                  properties={
                    (
                      section.properties[questionKey].properties as unknown as {
                        repeatingGroup: RepeatingProperties;
                      }
                    ).repeatingGroup
                  }
                  value={(value as unknown as Record<string, AnswerTypes>[]) || []}
                  errors={repeatingGroupErrors[questionKey]}
                  onChange={(entries) => {
                    if (!isPublicSubmission) {
                      markEditPending(questionKey);
                    }
                    debouncedUpdates[questionKey](questionType, questionKey, entries as unknown as AnswerTypes);
                    revalidateRepeatingGroup(questionKey, entries as unknown as Record<string, AnswerTypes>[]);
                  }}
                />
              </div>
            );
          }

          return (
            <div
              key={questionKey}
              data-testid={isAudits ? 'audit-question-row' : undefined}
              data-question-key={isAudits ? questionKey : undefined}
            >
              {title}

              {image && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    marginBottom: '16px',
                  }}
                >
                  <img
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    src={image.file as string}
                    alt="question"
                  />
                </div>
              )}
              <EntitiesFormField
                entityType={config.entityType}
                errorMessage={error ? error.join(', ') : undefined}
                requiredNotFilled={requiredNotFilled.includes(questionKey) || !!(error && error.length > 0)}
                items={section.properties[questionKey].properties?.enum as unknown as EnumOptions}
                value={value || ''}
                multiple={multiple}
                onChange={async (newValue: AnswerTypes, rawValue?: AnswerTypes, files?: File[]) => {
                  // Handle file uploads for attachment fields
                  if (files && files.length > 0) {
                    setPendingFiles(questionKey, files);
                  }

                  if (isPublicSubmission) {
                    debouncedUpdates[questionKey](questionType, questionKey, rawValue || newValue);
                  } else {
                    markEditPending(questionKey);
                    debouncedUpdates[questionKey](questionType, questionKey, newValue);
                  }
                }}
                description={description}
                type={questionType}
                lookupType={lookupType}
                fieldKey={questionKey}
                noFutureDate={noFutureDate}
              />
            </div>
          );
        })}
      </QuestionsList>
      {/* z-index set higher than HubSpot chat widget to prevent it from covering the activity feed send button */}
      {isAudits && (
        <Drawer
          anchor="right"
          open={!!openQuestionKey}
          onClose={closeDrawer}
          sx={{ zIndex: 1200 }}
          PaperProps={QUESTION_DRAWER_PAPER_PROPS}
          key={`${openQuestionKey}_drawer`}
        >
          {openQuestionKey && questionActivities && (
            <AuditQuestionDrawer
              activities={questionActivities}
              questionKey={openQuestionKey}
              questionLabel={section.properties[openQuestionKey]?.description || ''}
              tab={openTab}
              onTabChange={(tab) => openDrawer(openQuestionKey, tab)}
              onClose={closeDrawer}
              lookupRelations={lookupRelationsFor(openQuestionKey)}
              lookupModule={section.properties[openQuestionKey]?.properties?.lookupType as unknown as EntityModules}
              entityUuid={data?.entity || undefined}
            />
          )}
        </Drawer>
      )}
    </>
  );
};

export default observer(SubmissionSection);
